import type { ApplicantStatusChangedPayload } from '@/integrations/Sumsub/types';

type SubmissionStatus = 'init' | 'capture' | 'pending' | 'reviewing' | 'approved' | 'blocked';

interface ReviewResult {
  reviewAnswer: string;
  reviewRejectType?: string;
}

export class DocumentSubmissionMode {
  name: SubmissionStatus;

  constructor(props: { mode: SubmissionStatus }) {
    this.name = props.mode;
  }

  static Init = new DocumentSubmissionMode({ mode: 'init' });

  static Capture = new DocumentSubmissionMode({ mode: 'capture' });

  static Pending = new DocumentSubmissionMode({ mode: 'pending' });

  static Reviewing = new DocumentSubmissionMode({ mode: 'reviewing' });

  static Blocked = new DocumentSubmissionMode({ mode: 'blocked' });

  static Approved = new DocumentSubmissionMode({ mode: 'approved' });

  static isApprovedReviewResult(reviewResult: ReviewResult) {
    return reviewResult.reviewAnswer === 'GREEN';
  }

  static isRejectedReviewResult(reviewResult: ReviewResult) {
    return reviewResult.reviewAnswer === 'RED' && reviewResult.reviewRejectType === 'FINAL';
  }

  static isResubmissionRequiredReviewResult(reviewResult: ReviewResult) {
    return reviewResult.reviewAnswer === 'RED' && reviewResult.reviewRejectType === 'RETRY';
  }

  static mapCompletedReviewResult(payload: ApplicantStatusChangedPayload) {
    if (!payload || !payload.reviewResult) {
      throw new Error('Invalid review result provided');
    }
    if (DocumentSubmissionMode.isApprovedReviewResult(payload.reviewResult)) {
      return DocumentSubmissionMode.Approved;
    }
    if (DocumentSubmissionMode.isResubmissionRequiredReviewResult(payload.reviewResult)) {
      return DocumentSubmissionMode.Capture;
    }
    if (DocumentSubmissionMode.isRejectedReviewResult(payload.reviewResult)) {
      return DocumentSubmissionMode.Blocked;
    }

    throw new Error('Unexpected completion review result state');
  }

  // eslint-disable-next-line complexity
  static fromApplicantReviewResult(reviewResult?: ApplicantStatusChangedPayload) {
    if (!reviewResult || !reviewResult.reviewStatus) {
      throw new Error('Invalid review result provided');
    }

    switch (reviewResult?.reviewStatus) {
      case 'init':
        return DocumentSubmissionMode.Capture;
      case 'pending':
      case 'queued':
      case 'prechecked':
        return DocumentSubmissionMode.Pending;
      case 'onHold':
      case 'awaitingService':
      case 'awaitingUser':
        return DocumentSubmissionMode.Reviewing;
      case 'completed':
        return DocumentSubmissionMode.mapCompletedReviewResult(reviewResult);
    }
  }
}
